'use client';

import { Button, Label, TextInput } from '@petsmart-ui/sparky';
import { useEffect, useRef } from 'react';

import type { SearchBoxProps } from '../types';

export function SearchBox({ onKeyDown, onCancelClick, autocomplete, query, setAutocompleteQuery }: SearchBoxProps) {
    const enableSearch = typeof query === 'string' && query.trim() !== '';
    // NOTE: Search box should always be server-side rendered,
    // never be client-side rendered. Ok to be server-side
    // rendered and then hydrated, but the search bar
    // should always appear immediately on page load.
    // Refer to ticket SB-1525
    const refForm = useRef<HTMLFormElement | null>(null);
    const refSubmitButton = useRef<HTMLButtonElement | null>(null);
    const refSearchBoxInput = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (refForm.current) {
            refSubmitButton.current = refForm.current.querySelector('[type="submit"]');
            refSearchBoxInput.current = refForm.current.querySelector('[id="autocmplete-search-box"]');
        }
    }, [refForm]);

    const inputProps = autocomplete.getInputProps({
        id: 'autocmplete-search-box',
        'aria-labelledby': 'autocomplete-list',
        inputElement: null, // this parameter does not matter at all
    });

    return (
        <div className="autocomplete-searchbox" data-testid="searchbox">
            <form
                ref={refForm}
                className="sparky-c-inline-form"
                method="get"
                data-testid="search-form"
                {...autocomplete.getFormProps({
                    inputElement: null,
                })}
            >
                <Label
                    htmlFor="search-input"
                    className="sparky-c-inline-form__label sparky-u-is-vishidden"
                    text="Search"
                />

                <TextInput
                    data-testid="searchbox-input"
                    className="sparky-c-inline-form__input"
                    float={false}
                    {...inputProps}
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                        inputProps.onChange(e);
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        inputProps.onChange(e);
                        autocomplete.setQuery(e.currentTarget.value);
                        setAutocompleteQuery(e.currentTarget.value);
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            // TODO: remove this hack in place of something else
                            // could not find a way to submit the form without this due to
                            // autocomplete getting in the way?
                            if (refSubmitButton?.current) {
                                e.preventDefault();
                                e.stopPropagation();
                                refSubmitButton?.current?.click();
                            }
                        } else {
                            inputProps.onKeyDown(e);
                            onKeyDown(e);
                        }

                        return false;
                    }}
                    type="text"
                    placeholder="Search"
                    data-cs-encrypt
                    value={query}
                />

                {typeof query === 'string' && query.trim() !== '' && (
                    <Button
                        data-testid="search-box-close-button"
                        className="sparky-c-inline-form__close-button"
                        variant="primary"
                        text="Search"
                        iconName="x"
                        size="lg"
                        hideText
                        type="button"
                        onClick={() => {
                            autocomplete.setQuery('');
                            setAutocompleteQuery('');

                            // this is another hack to get the search box to actually reset when cleared.
                            if (refSearchBoxInput.current) {
                                setTimeout(() => {
                                    refSearchBoxInput?.current?.focus();
                                }, 100);
                            }
                        }}
                    />
                )}

                <Button
                    id="sparky-c-inline-form__button"
                    className="sparky-c-inline-form__button"
                    variant="primary"
                    text="Search"
                    iconName="search"
                    size="lg"
                    hideText
                    type={enableSearch ? 'submit' : 'button'}
                />
            </form>

            <Button
                className="autocomplete-cancel"
                text="Cancel"
                data-testid="cancel-button"
                onClick={onCancelClick}
                variant="link"
            />
        </div>
    );
}
